<script setup>
  import { ref, computed, useSlots } from 'vue';

  import CredereSpinner from '@/components/Core/Spinner.vue';

  const slots = useSlots();

  const props = defineProps({
    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    loading: {
      type: Boolean,
      default: false,
      required: false,
    },

    size: {
      type: String,
      default: 'medium',
      required: false,
      validator(value) {
        return ['small', 'medium', 'large'].includes(value);
      },
    },
    color: {
      type: String,
      default: 'primary',
      required: false,
      validator(value) {
        return ['primary', 'secondary', 'highlight', 'danger', 'light'].includes(value);
      },
    },

    href: {
      type: String,
      default: null,
      required: false,
    },

    variant: {
      type: String,
      default: 'A',
      required: false,
      validator(value) {
        return ['A', 'B', 'C'].includes(value);
      },
    },

    variantSecondary: {
      type: String,
      default: 'A',
      required: false,
      validator(value) {
        return ['A', 'B'].includes(value);
      },
    },

    // An icon-only button renders no text, so it has no accessible name of its own -
    // a screen reader announces "button" and nothing else. `label` supplies one, and
    // every iconed usage needs either this or a `title` (which falls through to the
    // root element and doubles as the hover tooltip). Text buttons leave it null and
    // take their name from the slot.
    label: {
      type: String,
      default: null,
      required: false,
    },

    icon: {
      type: String,
      required: false,
    },
    iconPosition: {
      type: String,
      default: 'left',
      required: false,
      validator(value) {
        return ['left', 'right'].includes(value);
      },
    },
    iconSize: {
      type: String,
      default: 'medium',
      required: false,
      validator(value) {
        return ['medium', 'large'].includes(value);
      },
    },
  });

  const emit = defineEmits(['click', 'blur', 'focus']);

  /*
   * O `disabled` DE UM `<a>` NÃO DESLIGA NADA, e é por isso que o guarda mora aqui.
   *
   * Com `href` este componente rende um `<a>` (ver o `:is` abaixo), e ali `disabled` é
   * atributo sem significado: o elemento continua clicável, continua focável e continua
   * navegando. Sem isto, o botão "Fazer simulação" de um veículo fora de estoque começava
   * uma proposta para ele - o cartão marcava `:disabled` e não adiantava.
   *
   * Para o `<button>` o navegador já barra o clique, e o guarda é inócuo. Ele para a
   * propagação junto porque um clique que não vale não deve alcançar quem está por baixo.
   */
  function click(e) {
    if (props.disabled || props.loading) {
      e.preventDefault();
      e.stopPropagation();

      return;
    }

    emit('click', e);
  }

  function focus(e) {
    emit('focus', e);
  }

  function blur(e) {
    emit('blur', e);
  }

  const { button_primary: _primary, button_secondary: _secondary } = window.CREDERE.variants || {};
  const variantPrimary = computed(() => _primary || props.variant);
  const variantSecondary = computed(() => _secondary || props.variantSecondary);

  const classes = computed(() => ({
    'credere-button': true,

    [`size--${props.size}`]: true,
    [`color--${props.color}`]: true,

    [`icon-size--${props.iconSize}`]: true,
    [`icon-position--${props.iconPosition}`]: true,

    'state--disabled': props.disabled,
    'state--loading': props.loading,

    'variant--iconed': props.icon && !slots.default,
    [`variant--button-1${variantPrimary.value?.toLowerCase()}`]: variantPrimary.value,
    [`variant--button-2${variantSecondary.value?.toLowerCase()}`]: props.color === 'secondary' && variantSecondary.value,
  }));

  /*
   * O PONTO DO SPINNER SE ESCOLHE PELO FUNDO DO BOTÃO, e por isso é uma tabela e não
   * uma condição.
   *
   * Carregando, o `state--loading` zera a cor do texto: os três pontos são o único
   * sinal que resta na tela, e um ponto da cor do fundo não é sinal nenhum. O Button
   * passava `light` fixo, o que deixava o botão claro carregando sem nada visível.
   *
   * O par de cada cor saiu de medir o contraste dos tons disponíveis contra o fundo
   * de cada botão, no vale da animação - os pontos descem a 50% de opacidade no meio
   * do ciclo, e é ali que o contraste é o pior do ciclo. Em todas as cinco cores o
   * vencedor no vale é também o vencedor com o ponto cheio, então não há empate a
   * desempatar. A tabela com os números está na story `Carregando`.
   */
  const SPINNER_VARIANTS = {
    primary: 'light',
    secondary: 'light',
    danger: 'light',
    highlight: 'dark',
    light: 'dark',
  };

  const spinnerVariant = computed(() => SPINNER_VARIANTS[props.color]);

  const refButton = ref();
  defineExpose({ refButton });
</script>

<template>
  <component
    ref="refButton"

    :is="href  ? 'a' : 'button'"
    :class="classes"
    :disabled="disabled || loading"
    :aria-disabled="disabled || loading"
    :aria-label="label"

    :href="href"

    @click="click"
    @blur="blur"
    @focus="focus"
  >
    <credere-spinner
      v-if="loading"
      class="credere-button-loading"
      :variant="spinnerVariant"
    />

    <font-awesome-icon
      v-if="icon"
      :icon="icon"
      class="credere-button-icon"
    />

    <slot />
  </component>
</template>

<style lang="scss">
  @import '@css/mixins/button.scss';

  .credere-button {
    @include credere-button;

    &.size--small {
      @include button-size(
        var(--cordel-font-size-s),
        var(--cordel-font-size-s),
        var(--cordel-spacing-micro) var(--cordel-spacing-xxxs),
        var(--cordel-spacing-nano),
      );
    }

    &.size--medium {
      @include button-size(
        var(--cordel-font-size-m),
        var(--cordel-font-size-s),
        var(--cordel-spacing-xxxs) var(--cordel-spacing-l),
        var(--cordel-spacing-xxxs),
      );
    }

    &.size--large {
      @include button-size(
        var(--cordel-font-size-l),
        var(--cordel-font-size-m),
        var(--cordel-spacing-xs) var(--cordel-spacing-giga),
        var(--cordel-spacing-xxxs),
      );
    }

    &.color--primary {
      @include button-color(
        var(--cordel-color-neutral-darker),
        var(--cordel-color-neutral-heavy),
        var(--cordel-color-constant-light),
      );
    }

    &.color--secondary {
      @include button-color(
        var(--cordel-color-neutral-dark),
        var(--cordel-color-constant-dark),
        var(--cordel-color-constant-light),
      );
    }

    &.color--highlight {
      @include button-color(
        var(--cordel-color-highlight-primary),
        var(--cordel-color-highlight-dark),
        var(--cordel-color-constant-dark),
      );
    }

    &.color--danger {
      @include button-color(
        var(--cordel-color-feedback-danger),
        var(--cordel-color-feedback-danger-dark),
        var(--cordel-color-constant-light),
      );
    }

    &.color--light {
      @include button-color(
        var(--cordel-color-neutral-light),
        var(--cordel-color-neutral-medium),
        var(--cordel-color-constant-dark),
      );
    }

    &.icon-position--right {
      flex-direction: row-reverse;
    }

    &.icon-size--large {
      .credere-button-icon {
        font-size: 1.25em;
      }
    }
  }

  .credere-button-loading {
    bottom: 0;
    left: 0;
    margin: auto;
    position: absolute;
    right: 0;
    top: 0;
  }
</style>
